import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Blocks } from 'lucide-react'
import { useAuth } from '../store/auth'
import { getBusinessModules, setBusinessModule } from '../api/modules'
import type { ModuleKey, ModuleStatus } from '../types'

const MODULE_LABELS: Record<ModuleKey, { label: string; description: string }> = {
  'mail-service':    { label: 'Mail',            description: 'Mailbox monitoring and captured emails' },
  'storage-service': { label: 'Storage',         description: 'Datasets, documents, and PDF templates' },
  'brainer':         { label: 'Brainer',         description: 'Collections, cups, plates, and dashboards' },
  'lens':            { label: 'Lens · ML',       description: 'No-code AutoML data sources and scenarios' },
  'invoice-facteur': { label: 'Invoice Facteur',  description: 'Electronic invoicing via PDP adapters (Flowie, ...)' },
  'order-parser':    { label: 'Order Parser',    description: 'Background PDF order-document parsing' },
  'raider-agent':    { label: 'Raider Agent',    description: 'Background AI document parsing' },
  'energy-os':       { label: 'EnergyOS',        description: 'AI operating system for distributed energy — devices, forecasting, optimization' },
  'garage':          { label: 'Garage',          description: 'Vehicle repair-shop operations — Refrigeration preorders, work orders, and stock transfers' },
}

function ModuleRow({ status, onToggle, saving }: { status: ModuleStatus; onToggle: (m: ModuleKey, enabled: boolean) => void; saving: boolean }) {
  const meta = MODULE_LABELS[status.moduleKey]
  return (
    <tr>
      <td>
        <p className="text-sm font-medium text-ink">{meta.label}</p>
        <p className="text-xs text-ink-dim mt-0.5">{meta.description}</p>
      </td>
      <td className="text-right">
        <button
          onClick={() => onToggle(status.moduleKey, !status.isEnabled)}
          disabled={saving}
          role="switch"
          aria-checked={status.isEnabled}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
            status.isEnabled ? 'bg-accent' : 'bg-paper-border'
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
              status.isEnabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
            }`}
          />
        </button>
      </td>
    </tr>
  )
}

export function Modules() {
  const { user } = useAuth()
  const businessId = user!.businessId
  const qc = useQueryClient()

  const { data: statuses = [], isLoading } = useQuery({
    queryKey: ['business-modules', businessId],
    queryFn: () => getBusinessModules(businessId).then((r) => r.data),
  })

  const toggle = useMutation({
    mutationFn: ({ moduleKey, isEnabled }: { moduleKey: ModuleKey; isEnabled: boolean }) =>
      setBusinessModule(businessId, moduleKey, isEnabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business-modules', businessId] }),
  })

  return (
    <div>
      <div className="mb-3">
        <h1 className="text-xl font-semibold text-ink">Modules</h1>
        <p className="text-sm text-ink-dim mt-0.5">
          Turn services on or off for your business. Changes apply the next time a user's session refreshes.
        </p>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : statuses.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-dim flex flex-col items-center gap-2">
            <Blocks size={20} className="text-ink-dim" />
            No modules found
          </div>
        ) : (
          <table className="data-table">
            <tbody>
              {statuses.map((s: ModuleStatus) => (
                <ModuleRow
                  key={s.moduleKey}
                  status={s}
                  saving={toggle.isPending}
                  onToggle={(moduleKey, isEnabled) => toggle.mutate({ moduleKey, isEnabled })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
